import {
  ALERT_TIME_ZONE,
  localParts,
  validMaintenancePlan,
  validMaintenanceRecord,
  type KysFr11Row,
  type MaintenancePlan,
  type MaintenanceRecord,
  type ReportResult,
  type Site,
  type TenantRole,
  type Vehicle,
} from "@fts/core";
import { DataError, type ChecklistFault, type MaintenancePlanInput, type MaintenanceRecordInput } from "./provider";

/*
 * SAMPLE DATA ONLY — the FR-11 maintenance plan and records of the demo mode (ADR 0033). Mirrors the rules
 * of supabase/migrations/20270524000001_kys_forms.sql: whoever sees the vehicle's site reads, fleet managers+
 * of the site write; the responsible party is a role, never a person.
 */

export type DemoKysAccess = {
  vehicles(): Vehicle[];
  sites(): Site[];
  hasTenantRole(tenantId: string, min: TenantRole): boolean;
  canAccessSite(siteId: string): boolean;
  canManageSite(siteId: string, min: TenantRole): boolean;
  faults(tenantId: string): ChecklistFault[];
  now(): number;
  nextId(prefix: string): string;
};

const clone = <T>(x: T): T => JSON.parse(JSON.stringify(x)) as T;
const DAY = 86_400_000;

export class DemoKys {
  private plans: MaintenancePlan[] = [];
  private records: MaintenanceRecord[] = [];

  constructor(private readonly a: DemoKysAccess) {
    this.seed();
  }

  /** Fictional plans and dates for the pilot workshop tenant: one overdue, one due soon, one never inspected. */
  private seed() {
    const t1 = this.a.vehicles()[0]?.tenantId;
    const vs = this.a.vehicles().filter((v) => v.tenantId !== t1);
    const day = (daysAgo: number) => new Date(this.a.now() - daysAgo * DAY).toISOString().slice(0, 10);
    const at = new Date(this.a.now()).toISOString();
    const seed: [string, number, MaintenancePlan["responsibleRole"], number | null, number | null][] = [
      // fleet no, interval, role, last periodic (days ago), last inspection (days ago)
      ["FL-01", 6, "workshop_chief", 40, 120],
      ["FL-02", 6, "workshop_chief", 170, 300],
      ["FL-03", 6, "technician", 200, 380],
      ["FL-04", 3, "external_service", 20, null],
    ];
    for (const [fleetNo, interval, role, periodic, inspection] of seed) {
      const v = vs.find((x) => x.fleetNo === fleetNo);
      if (!v) continue;
      this.plans.push({ vehicleId: v.id, tenantId: v.tenantId, intervalMonths: interval, responsibleRole: role, updatedAt: at });
      const base = { tenantId: v.tenantId, vehicleId: v.id, createdAt: at, voidsRecordId: null, voidReason: null, voided: false };
      if (periodic !== null) this.records.push({ ...base, id: this.a.nextId("mr"), kind: "periodic", doneOn: day(periodic), note: "Örnek: periyodik bakım" });
      if (inspection !== null) this.records.push({ ...base, id: this.a.nextId("mr"), kind: "annual_inspection", doneOn: day(inspection), note: null });
    }
  }

  private vehicle(id: string): Vehicle {
    const v = this.a.vehicles().find((x) => x.id === id);
    if (!v || !this.a.canAccessSite(v.siteId)) throw new DataError("forbidden");
    return v;
  }

  listRecords(vehicleId: string): MaintenanceRecord[] {
    this.vehicle(vehicleId);
    const voided = new Set(this.records.map((r) => r.voidsRecordId).filter(Boolean));
    return clone(this.records.filter((r) => r.vehicleId === vehicleId).map((r) => ({ ...r, voided: voided.has(r.id) })).sort((x, y) => y.doneOn.localeCompare(x.doneOn)));
  }

  savePlan(input: MaintenancePlanInput): void {
    const v = this.vehicle(input.vehicleId);
    if (!this.a.canManageSite(v.siteId, "fleet_manager")) throw new DataError("forbidden");
    if (!validMaintenancePlan(input)) throw new DataError("invalid_maintenance");
    const row: MaintenancePlan = { vehicleId: v.id, tenantId: v.tenantId, intervalMonths: input.intervalMonths, responsibleRole: input.responsibleRole, updatedAt: new Date(this.a.now()).toISOString() };
    this.plans = [...this.plans.filter((p) => p.vehicleId !== v.id), row];
  }

  addRecord(input: MaintenanceRecordInput): MaintenanceRecord {
    const v = this.vehicle(input.vehicleId);
    if (!this.a.canManageSite(v.siteId, "fleet_manager")) throw new DataError("forbidden");
    const note = input.note?.trim() || null;
    const voidReason = input.voidReason?.trim() || null;
    // Like the guard: never after today's Istanbul date; a correction voids one earlier, not yet voided record.
    const today = localParts(new Date(this.a.now()), ALERT_TIME_ZONE).date;
    if (!validMaintenanceRecord({ ...input, note, voidReason }, today)) throw new DataError("invalid_maintenance");
    if (input.voidsRecordId) {
      const old = this.records.find((r) => r.id === input.voidsRecordId);
      if (!old || old.vehicleId !== v.id || this.records.some((r) => r.voidsRecordId === old.id)) throw new DataError("invalid_maintenance");
    }
    const row: MaintenanceRecord = {
      id: this.a.nextId("mr"), tenantId: v.tenantId, vehicleId: v.id, kind: input.kind, doneOn: input.doneOn, note, createdAt: new Date(this.a.now()).toISOString(),
      voidsRecordId: input.voidsRecordId ?? null, voidReason, voided: false,
    };
    this.records.push(row);
    return clone(row);
  }

  private footers = new Map<string, string>();
  footer(tenantId: string): string | null {
    if (!this.a.hasTenantRole(tenantId, "viewer")) throw new DataError("forbidden");
    return this.footers.get(tenantId) ?? null;
  }
  setFooter(tenantId: string, text: string | null): void {
    if (!this.a.hasTenantRole(tenantId, "admin")) throw new DataError("forbidden");
    const v = text?.trim() ?? "";
    if (v.length > 200) throw new DataError("invalid_form");
    if (v) this.footers.set(tenantId, v);
    else this.footers.delete(tenantId);
  }

  /** Like report_kys_fr11(): every visible vehicle of the scope with its plan, last dates and open faults. */
  fr11(q: { tenantId: string; siteId: string | null }, tz: string): ReportResult<KysFr11Row> {
    if (!this.a.hasTenantRole(q.tenantId, "viewer")) throw new DataError("forbidden");
    const inScope = (siteId: string) => {
      if (!q.siteId) return true;
      let cur: string | null = siteId;
      for (let n = 0; cur && n < 64; n++) {
        if (cur === q.siteId) return true;
        cur = this.a.sites().find((x) => x.id === cur)?.parentId ?? null;
      }
      return false;
    };
    const faults = this.a.faults(q.tenantId).filter((f) => f.status === "open");
    const voided = new Set(this.records.map((r) => r.voidsRecordId).filter(Boolean));
    const last = (vehicleId: string, kind: MaintenanceRecord["kind"]) =>
      this.records.filter((r) => r.vehicleId === vehicleId && r.kind === kind && !voided.has(r.id)).map((r) => r.doneOn).sort().pop() ?? null;
    const rows = this.a
      .vehicles()
      .filter((v) => v.tenantId === q.tenantId && this.a.canAccessSite(v.siteId) && inScope(v.siteId))
      .sort((x, y) => x.fleetNo.localeCompare(y.fleetNo))
      .map<KysFr11Row>((v) => {
        const p = this.plans.find((x) => x.vehicleId === v.id);
        return {
          vehicleId: v.id,
          fleetNo: v.fleetNo,
          vehicleType: v.type,
          power: v.power,
          make: v.make,
          model: v.model,
          serialNo: v.serialNo,
          siteId: v.siteId,
          siteName: this.a.sites().find((s) => s.id === v.siteId)?.name ?? null,
          active: v.active,
          intervalMonths: p?.intervalMonths ?? null,
          responsibleRole: p?.responsibleRole ?? null,
          lastPeriodicOn: last(v.id, "periodic"),
          lastInspectionOn: last(v.id, "annual_inspection"),
          lastRepairOn: last(v.id, "repair"),
          openFaults: faults.filter((f) => f.vehicleId === v.id).length,
          openCritical: faults.filter((f) => f.vehicleId === v.id && f.critical).length,
        };
      });
    return { rows, tz, truncated: false };
  }
}
